import { useState } from 'react';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';

import { INPUT_DEBOUNCE_TIME } from 'Utilities/consts';

type SearchTerm = string | undefined;

interface UseKeywordSearchProps {
	debounceDelay?: number;
	initialKeyword?: SearchTerm;
	minChars?: number;
	searchFunction: ({ keyword }: { keyword: SearchTerm }) => void;
}

interface UseKeywordSearchReturn {
	onClear: () => void;
	onSearch: (searchTerm: SearchTerm) => void;
	reset: () => void;
	searchValue?: string;
}

const useKeywordSearch = ({
	debounceDelay = INPUT_DEBOUNCE_TIME,
	initialKeyword = undefined,
	minChars = 2,
	searchFunction,
}: UseKeywordSearchProps): UseKeywordSearchReturn => {
	const [lastText, setLastText] = useState(initialKeyword);
	const [lastCallbackSearch, setLastCallbackSearch] = useState(undefined);

	const clearState = () => {
		setLastText(undefined);
		setLastCallbackSearch(undefined);
	};

	const searchByKeyword = (searchTerm: SearchTerm) => {
		searchFunction({ keyword: searchTerm });
	};

	const debouncedSearch = debounce((searchTerm) => {
		const textLength = searchTerm.length || 0;
		if (!isEmpty(searchTerm)) {
			setLastText(searchTerm);
		}
		const isLessThanMinCharacterCount = textLength > 0 && textLength < minChars;
		const isNoInputAndNoPreviousInput = !searchTerm && !lastText;
		const isSameSearchAsLast = lastCallbackSearch === searchTerm;

		if (isLessThanMinCharacterCount || isNoInputAndNoPreviousInput || isSameSearchAsLast) {
			return;
		}

		searchByKeyword(searchTerm);

		if (isEmpty(searchTerm)) {
			clearState();
		} else {
			setLastCallbackSearch(searchTerm);
		}
	}, debounceDelay);

	const onSearch = (searchTerm: SearchTerm) => {
		debouncedSearch(searchTerm);
	};

	const onClear = () => {
		clearState();
		searchByKeyword(undefined);
	};

	return { onClear, onSearch, reset: clearState, searchValue: lastCallbackSearch };
};

export default useKeywordSearch;
